<script setup>
import { ref, computed, inject } from 'vue'
import DropdownMenu from '@/components/Dropdown/DropdownMenu.vue'
import { useAnchoredMenu } from '@/composables/useAnchoredMenu'

const props = defineProps({
  icon: {
    type: Array,
    required: true
  },
  label: {
    type: String,
    required: true
  },
  headerLabel: {
    type: String,
    default: null
  },
  headerSubtext: {
    type: String,
    default: null
  },
  disabled: {
    type: Boolean,
    default: false
  },
  loading: {
    type: Boolean,
    default: false
  },
  loadingText: {
    type: String,
    default: 'Loading...'
  },
  sidebarExpanded: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['open', 'close'])

const MENU_WIDTH = 280

const isOpen = ref(false)
const triggerRef = ref(null)
const menuComp = ref(null)

const displayHeaderLabel = computed(() => props.headerLabel || props.label)

// Expanded state comes from the enclosing ToolRail via provide/inject. Falls back
// to the `sidebarExpanded` prop for rails not yet on ToolRail (e.g. IpTableSidebar).
const railExpanded = inject('railExpanded', null)
const effectiveExpanded = computed(() =>
  railExpanded ? railExpanded.value : props.sidebarExpanded
)

function close() {
  if (!isOpen.value) return
  isOpen.value = false
  emit('close')
}

function toggle() {
  if (props.disabled) return
  isOpen.value = !isOpen.value
  emit(isOpen.value ? 'open' : 'close')
}

// Rail menus open to the side, clear of the rail itself.
const { menuStyle } = useAnchoredMenu({
  anchorRef: triggerRef,
  menuRef: () => menuComp.value?.el,
  isOpen,
  placement: 'left',
  width: MENU_WIDTH,
  onDismiss: close
})

defineExpose({ close, isOpen })
</script>

<template>
  <li class="nav-item-wrapper sidebar-dropdown-wrapper">
    <div
      ref="triggerRef"
      class="sidebar-nav"
      :class="{ 'disabled': disabled, 'active': isOpen }"
      @click="toggle"
    >
      <font-awesome-icon class="icon" :icon="icon" />
      <span class="nav-text">{{ label }}</span>
      <div class="nav-popout" v-if="!effectiveExpanded && !isOpen">
        <span>{{ label }}</span>
      </div>
    </div>

    <DropdownMenu
      ref="menuComp"
      :show="isOpen"
      :header-label="displayHeaderLabel"
      :header-subtext="headerSubtext"
      :loading="loading"
      :loading-text="loadingText"
      :menu-style="menuStyle"
    >
      <slot></slot>
    </DropdownMenu>
  </li>
</template>

<style scoped>
.sidebar-dropdown-wrapper {
  position: relative;
  padding: 0.25rem 0.75rem;
}

.sidebar-nav {
  padding: 0.75rem 1rem;
  height: auto;
  min-height: 3rem;
  color: var(--text-secondary);
  display: flex;
  align-items: center;
  transition: var(--transition);
  border-radius: var(--radius-lg);
  position: relative;
  gap: 0;
  cursor: pointer;
}

.sidebar-nav.disabled {
  color: var(--text-disabled);
  cursor: not-allowed;
}

.sidebar-nav.active {
  background-color: var(--surface-hover);
  color: white;
}

.sidebar-nav:not(.disabled):hover {
  color: white;
  background-color: var(--surface-hover);
}

.sidebar-nav.disabled:hover {
  background-color: var(--surface-page);
}

.sidebar-nav .icon {
  font-size: 1.1rem;
  width: 1.2rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.nav-text {
  display: none;
}

.nav-popout {
  display: flex;
  position: absolute;
  right: calc(100% - 1.3rem);
  left: auto;
  top: 0;
  height: 100%;
  background: var(--surface-hover);
  padding: 0.75rem 1rem;
  border-radius: var(--radius-lg);
  align-items: center;
  white-space: nowrap;
  z-index: 100;
  color: white;
  font-size: 0.95rem;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s ease;
  text-decoration: none;
}

.sidebar-dropdown-wrapper:hover .nav-popout {
  opacity: 1;
  pointer-events: auto;
}

/* Muted popout for disabled items */
.sidebar-nav.disabled .nav-popout {
  background: var(--surface-page);
  color: var(--text-disabled);
}

/* Hide popout when dropdown is open */
.sidebar-nav.active + .nav-popout,
.sidebar-nav.active .nav-popout {
  opacity: 0 !important;
  pointer-events: none !important;
}
</style>

<!-- Styles for expanded sidebar state - applied from parent -->
<style>
/* When sidebar is expanded */
#tool-sidebar.expanded .sidebar-dropdown-wrapper .sidebar-nav {
  justify-content: flex-start;
}

#tool-sidebar.expanded .sidebar-dropdown-wrapper .nav-text {
  display: inline;
  white-space: nowrap;
  font-size: 0.95rem;
}

#tool-sidebar.expanded .sidebar-dropdown-wrapper .sidebar-nav .icon {
  margin-right: 0.75rem;
}

#tool-sidebar:not(.expanded) .sidebar-dropdown-wrapper .sidebar-nav {
  justify-content: center;
  padding: 0.75rem 0;
  width: 100%;
}
</style>
